/**
 * Automation Envelope 系统
 * 用于在音频轨道上创建和编辑自动化曲线
 */

// 曲线类型枚举
export enum CurveType {
  LINEAR = 'linear',           // 线性
  EXPONENTIAL = 'exponential', // 指数
  LOGARITHMIC = 'logarithmic', // 对数
  SMOOTH = 'smooth',           // 平滑（S曲线）
  STEP = 'step',               // 阶梯
}

// Automation 点接口
export interface AutomationPoint {
  time: number      // 时间（秒）
  value: number     // 值（0-1）
  curveType: CurveType  // 到下一个点的曲线类型
}

/**
 * Automation Envelope 类
 * 管理自动化点和插值计算
 */
export class AutomationEnvelope {
  private points: AutomationPoint[] = []
  
  constructor(initialPoints: AutomationPoint[] = []) {
    this.points = [...initialPoints].sort((a, b) => a.time - b.time)
  }
  
  /**
   * 添加自动化点
   */
  addPoint(point: AutomationPoint): void {
    this.points.push(point)
    this.points.sort((a, b) => a.time - b.time)
  }
  
  /**
   * 移除自动化点
   */
  removePoint(index: number): void {
    if (index >= 0 && index < this.points.length) {
      this.points.splice(index, 1)
    }
  }
  
  /**
   * 更新自动化点
   */
  updatePoint(index: number, point: Partial<AutomationPoint>): void {
    if (index >= 0 && index < this.points.length) {
      this.points[index] = { ...this.points[index], ...point }
      // 如果时间改变了，需要重新排序
      if (point.time !== undefined) {
        this.points.sort((a, b) => a.time - b.time)
      }
    }
  }
  
  /**
   * 获取所有点
   */
  getPoints(): AutomationPoint[] {
    return [...this.points]
  }
  
  /**
   * 设置所有点
   */
  setPoints(points: AutomationPoint[]): void {
    this.points = [...points].sort((a, b) => a.time - b.time)
  }
  
  /**
   * 清空所有点
   */
  clear(): void {
    this.points = []
  }
  
  /**
   * 在指定时间获取插值后的值
   * 实现了 C++ 的 Interpolate 算法
   */
  getValueAtTime(currentTime: number): number {
    if (this.points.length === 0) {
      return 0.5 // 默认中性值
    }
    
    if (this.points.length === 1) {
      return this.points[0].value
    }
    
    // 如果时间在第一个点之前
    if (currentTime <= this.points[0].time) {
      return this.points[0].value
    }
    
    // 如果时间在最后一个点之后
    if (currentTime >= this.points[this.points.length - 1].time) {
      return this.points[this.points.length - 1].value
    }
    
    // 找到当前时间所在的区间
    for (let i = 0; i < this.points.length - 1; i++) {
      const current = this.points[i]
      const next = this.points[i + 1]
      
      if (currentTime >= current.time && currentTime <= next.time) {
        return this.interpolate(current, next, currentTime)
      }
    }
    
    return this.points[this.points.length - 1].value
  }
  
  /**
   * 在两个点之间进行插值
   * 移植自 C++ 的 AutomationPoint::Interpolate 方法
   */
  private interpolate(
    current: AutomationPoint,
    next: AutomationPoint,
    currentTime: number
  ): number {
    if (currentTime <= current.time) return current.value
    if (currentTime >= next.time) return next.value
    
    const timeDiff = next.time - current.time
    const valueDiff = next.value - current.value
    const t = (currentTime - current.time) / timeDiff
    
    switch (current.curveType) {
      case CurveType.LINEAR:
        return current.value + valueDiff * t
      
      case CurveType.EXPONENTIAL:
        // 指数曲线：exp(t * 2.302585) - 1.0) / 9.0
        // 2.302585 = ln(10)
        return current.value + valueDiff * (Math.exp(t * 2.302585) - 1.0) / 9.0
      
      case CurveType.LOGARITHMIC:
        // 对数曲线：log(1.0 + t * 9.0) / 2.302585
        return current.value + valueDiff * Math.log(1.0 + t * 9.0) / 2.302585
      
      case CurveType.SMOOTH:
        // 平滑插值（Hermite插值）：t^2 * (3 - 2t)
        const smoothT = t * t * (3.0 - 2.0 * t)
        return current.value + valueDiff * smoothT
      
      case CurveType.STEP:
        // 阶梯式，保持当前值
        return current.value
      
      default:
        return current.value + valueDiff * t
    }
  }
  
  /**
   * 生成曲线上的采样点（用于绘制）
   */
  generateCurve(sampleRate: number = 100): Array<{ time: number; value: number }> {
    if (this.points.length === 0) return []
    if (this.points.length === 1) {
      return [{ time: this.points[0].time, value: this.points[0].value }]
    }
    
    const startTime = this.points[0].time
    const endTime = this.points[this.points.length - 1].time
    const duration = endTime - startTime
    const step = duration / sampleRate
    
    const curve: Array<{ time: number; value: number }> = []
    
    for (let i = 0; i <= sampleRate; i++) {
      const time = startTime + i * step
      const value = this.getValueAtTime(time)
      curve.push({ time, value })
    }
    
    return curve
  }
  
  /**
   * 查找距离指定时间最近的点
   */
  findNearestPoint(time: number, threshold: number = 0.5): number {
    let nearestIndex = -1
    let minDistance = threshold
    
    for (let i = 0; i < this.points.length; i++) {
      const distance = Math.abs(this.points[i].time - time)
      if (distance < minDistance) {
        minDistance = distance
        nearestIndex = i
      }
    }
    
    return nearestIndex
  }
}

/**
 * 创建预设的 Automation Envelope
 */
export class AutomationPresets {
  /**
   * Filter 预设：Low Pass Out（低通淡出）
   */
  static filterLowPassOut(startTime: number, endTime: number): AutomationEnvelope {
    const envelope = new AutomationEnvelope()
    const duration = endTime - startTime
    
    envelope.addPoint({ time: startTime, value: 0.5, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: startTime + duration * 0.3, value: 0.5, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: startTime + duration * 0.7, value: 0.2, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: endTime, value: 0.5, curveType: CurveType.SMOOTH })
    
    return envelope
  }
  
  /**
   * Filter 预设：Low Pass In（低通淡入）
   */
  static filterLowPassIn(startTime: number, endTime: number): AutomationEnvelope {
    const envelope = new AutomationEnvelope()
    const duration = endTime - startTime
    
    envelope.addPoint({ time: startTime, value: 0.2, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: startTime + duration * 0.3, value: 0.5, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: startTime + duration * 0.7, value: 0.5, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: endTime, value: 0.5, curveType: CurveType.SMOOTH })
    
    return envelope
  }
  
  /**
   * Filter 预设：High Pass Out（高通淡出）
   */
  static filterHighPassOut(startTime: number, endTime: number): AutomationEnvelope {
    const envelope = new AutomationEnvelope()
    const duration = endTime - startTime
    
    envelope.addPoint({ time: startTime, value: 0.5, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: startTime + duration * 0.3, value: 0.5, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: startTime + duration * 0.7, value: 0.8, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: endTime, value: 0.5, curveType: CurveType.SMOOTH })
    
    return envelope
  }
  
  /**
   * Filter 预设：High Pass In（高通淡入）
   */
  static filterHighPassIn(startTime: number, endTime: number): AutomationEnvelope {
    const envelope = new AutomationEnvelope()
    const duration = endTime - startTime
    
    envelope.addPoint({ time: startTime, value: 0.8, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: startTime + duration * 0.3, value: 0.5, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: startTime + duration * 0.7, value: 0.5, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: endTime, value: 0.5, curveType: CurveType.SMOOTH })
    
    return envelope
  }
  
  /**
   * Volume 预设：Fade Out
   */
  static volumeFadeOut(startTime: number, endTime: number): AutomationEnvelope {
    const envelope = new AutomationEnvelope()
    
    envelope.addPoint({ time: startTime, value: 1.0, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: endTime, value: 0.0, curveType: CurveType.SMOOTH })
    
    return envelope
  }
  
  /**
   * Volume 预设：Fade In
   */
  static volumeFadeIn(startTime: number, endTime: number): AutomationEnvelope {
    const envelope = new AutomationEnvelope()
    
    envelope.addPoint({ time: startTime, value: 0.0, curveType: CurveType.SMOOTH })
    envelope.addPoint({ time: endTime, value: 1.0, curveType: CurveType.SMOOTH })
    
    return envelope
  }
  
  /**
   * 创建空白 Envelope（中性值）
   */
  static neutral(startTime: number, endTime: number): AutomationEnvelope {
    const envelope = new AutomationEnvelope()
    
    envelope.addPoint({ time: startTime, value: 0.5, curveType: CurveType.LINEAR })
    envelope.addPoint({ time: endTime, value: 0.5, curveType: CurveType.LINEAR })
    
    return envelope
  }
  
  /**
   * 创建平坦的 Envelope（用于 EQ，0.5 表示 0dB）
   */
  static flat(startTime: number, endTime: number): AutomationEnvelope {
    const envelope = new AutomationEnvelope()
    
    envelope.addPoint({ time: startTime, value: 0.5, curveType: CurveType.LINEAR })
    envelope.addPoint({ time: endTime, value: 0.5, curveType: CurveType.LINEAR })
    
    return envelope
  }
}

